import React from 'react';
import { Field, useFormikContext } from 'formik';

import { InputUI, FormErrorUI, FormLabelUI, AutoCompleteSelectUI, CheckboxUI } from '../Interface';
import useConfig from '../../context/ConfigContext';
import useParam from '../../context/ParamContext';

const FormFields = ({ viewField, autoCompleteOptions, selectedValues, autocompleteData }) => {
  const { config } = useConfig();
  const { params } = useParam();
  const { values, errors, setFieldValue, validateField } = useFormikContext();

  const isMobileEnabled = Boolean(values.useMobileIncoming || values.useMobileOutGoing);

  const mobilTargetStatusLabel = (autoCompleteOptions?.prodStatus ?? []).find(
    (opt) => opt?.value === values?.mobilTargetStatus
  )?.label;

  const filteredResourceOptions = (() => {
    if (!values.resourceOneGroupId) {
      return autoCompleteOptions.resourceOne;
    }

    const allowedResourceIds = new Set(
      (autocompleteData?.resourceOne ?? [])
        .filter((resource) => resource.resourceOneGroupId === values.resourceOneGroupId)
        .map((resource) => resource.id)
    );

    return (autoCompleteOptions.resourceOne ?? []).filter((option) => allowedResourceIds.has(option.value));
  })();

  return (
    <div className="grid grid-cols-4 gap-x-4 gap-y-4 mx-4 mb-5">
      <div className="col-span-4 md:col-span-1">
        <Field
          type="text"
          name="code"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Művelet kódja" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.code} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.code && values.code !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-2">
        <Field
          type="text"
          name="operationName"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Művelet neve" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.operationName} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.operationName && values.operationName !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="resourceOneGroupId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Gépcsoport" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.resourceOneGroupId} />}
          onChange={(_e, newVal) => {
            setFieldValue('resourceOneGroupId', newVal?.value ?? null).then(() => {
              validateField('resourceOneGroupId');
            });

            setFieldValue('resourceOneId', null).then(() => {
              validateField('resourceOneId');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.resourceOneGroupId !== '' }}
          selectedValue={values.resourceOneGroupId}
          selectedLabelValue={selectedValues?.resourceOneGroup?.name}
          dataset={autoCompleteOptions.resourceOneGroup}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="resourceOneId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Gép" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.resourceOneId} />}
          onChange={(_e, newVal) => {
            setFieldValue('resourceOneId', newVal?.value ?? null).then(() => {
              validateField('resourceOneId');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.resourceOneId !== '' }}
          selectedValue={values.resourceOneId}
          selectedLabelValue={selectedValues?.resourceOne?.name}
          dataset={filteredResourceOptions}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="companyLocationId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Telephely" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.companyLocationId} />}
          onChange={(_e, newVal) => {
            setFieldValue('companyLocationId', newVal?.value ?? null).then(() => {
              validateField('companyLocationId');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.companyLocationId !== '' }}
          selectedValue={values.companyLocationId}
          selectedLabelValue={selectedValues?.companyLocation?.name}
          dataset={autoCompleteOptions.companyLocation}
        />
      </div>
      {params.OPERATION_MULTI_MAN_FIELD === 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="number"
            name="multiManOperation"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Többemberes művelet" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.multiManOperation} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.multiManOperation && values.multiManOperation !== '' }}
          />
        </div>
      )}
      <div className="col-span-4 md:col-span-1">
        <Field
          type="number"
          name="costPerHour"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Költség /óra" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.costPerHour} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.costPerHour && values.costPerHour !== '' }}
        />
      </div>
      {params.OPERATION_GROUP_REPORTING_FIELD === 'Y' && (
        <div className="md:col-span-1 col-span-4 pt-3">
          <Field
            name="groupReporting"
            type="checkbox"
            as={CheckboxUI}
            fullWidth
            label={<FormLabelUI text="Csoportos lejelentés" />}
            disabled={viewField}
          />
        </div>
      )}
      {params.OPERATION_VERIFICATION_FIELD === 'Y' && (
        <div className="md:col-span-1 col-span-4">
          <Field
            name="verificationRequired"
            type="checkbox"
            as={CheckboxUI}
            fullWidth
            label={<FormLabelUI text="Ellenőrzés szükséges" />}
            disabled={viewField}
          />
        </div>
      )}
      <div className="md:col-span-2 col-span-4">
        <Field
          name="showMaterialItemCode"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Alapanyag cikkszám megjelenítése" />}
          disabled={viewField}
        />
      </div>
      <div className="md:col-span-1 col-span-4">
        <Field
          name="showDoseNumber"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Adagszám megadása" />}
          disabled={viewField}
        />
      </div>

      <div className="md:col-span-1 col-span-4">
        <Field
          name="useMobileIncoming"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Mobil bizonylat (visszavételezés)" />}
          disabled={viewField}
          onChange={(event) => {
            const checked = event.target.checked;
            setFieldValue('useMobileIncoming', checked).then(() => {
              validateField('useMobileIncoming');
              validateField('mobilTargetStatus');
            });

            const other = Boolean(values.useMobileOutGoing);
            if (!checked && !other) {
              setFieldValue('mobilTargetStatus', null).then(() => validateField('mobilTargetStatus'));
            }
          }}
        />
      </div>

      <div className="md:col-span-1 col-span-4">
        <Field
          name="useMobileOutGoing"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Mobil bizonylat (kiadás)" />}
          disabled={viewField}
          onChange={(event) => {
            const checked = event.target.checked;
            setFieldValue('useMobileOutGoing', checked).then(() => {
              validateField('useMobileOutGoing');
              validateField('mobilTargetStatus');
            });

            const other = Boolean(values.useMobileIncoming);
            if (!checked && !other) {
              setFieldValue('mobilTargetStatus', null).then(() => validateField('mobilTargetStatus'));
            }
          }}
        />
      </div>

      <div className="col-span-4 md:col-span-2">
        <Field
          type="autocomplete"
          name="mobilTargetStatus"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Mobil cél státusz" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.mobilTargetStatus} />}
          onChange={(_e, newVal) => {
            setFieldValue('mobilTargetStatus', newVal?.value ?? null).then(() => {
              validateField('mobilTargetStatus');
            });
          }}
          disabled={viewField || !isMobileEnabled}
          InputLabelProps={{ shrink: values.mobilTargetStatus !== '' }}
          selectedValue={values.mobilTargetStatus}
          selectedLabelValue={mobilTargetStatusLabel ?? selectedValues?.mobilTargetStatusName ?? ''}
          dataset={autoCompleteOptions.prodStatus}
        />
      </div>
      {config.operationSequence && (
        <div className="md:col-span-2 col-span-4">
          <Field
            name="ignoreSequence"
            type="checkbox"
            as={CheckboxUI}
            fullWidth
            label={<FormLabelUI text="Sorrendiség figyelmen kívül hagyása" />}
            disabled={viewField}
          />
        </div>
      )}
    </div>
  );
};

export default FormFields;
